import { FieldValues } from 'react-hook-form';

import { generatePassword } from '@src/common';
import { CountryCode, isMercoSulExcludingBrazil } from '@src/common/auth';
import { Buyer, SignupRequest, UserRequest } from '@src/core/buyerApiClient';
import { cpf } from 'cpf-cnpj-validator';

export enum IdentityType {
  CPF = 'cpf',
  CNPJ = 'cnpj',
}

export enum ForeignIdentityType {
  PASSPORT = 'passport',
  FOREIGN_ID = 'identity',
}

const getDefaultForeignIdentityType = (
  identityType: string | null,
  isForeignBuyer: boolean,
  countryCode: string | null,
) => {
  if (isForeignBuyer && isMercoSulExcludingBrazil(countryCode ?? '')) {
    return ForeignIdentityType.FOREIGN_ID;
  }

  if (isForeignBuyer && !isMercoSulExcludingBrazil(countryCode ?? '')) {
    return ForeignIdentityType.PASSPORT;
  }

  if (!identityType) {
    return null;
  }

  if (identityType && (identityType === IdentityType.CPF || identityType === IdentityType.CNPJ)) {
    return null;
  }

  return identityType;
};

export const getDefaultValues = (buyer: Buyer | undefined, fillData: boolean, isForeignBuyer: boolean) => {
  if (fillData && buyer) {
    return {
      email: buyer.email,
      emailConfirmation: '',
      firstName: buyer.firstName,
      lastName: buyer.lastName,
      phone: buyer.phone ?? '+55',
      identity: buyer.identity,
      corporateName: buyer.corporateName,
      countryCode: buyer.countryCode ?? CountryCode.BRAZIL,
      identityType: isForeignBuyer || cpf.isValid(buyer.identity ?? '') ? IdentityType.CPF : IdentityType.CNPJ,
      birthdate: buyer.birthdate,
      agreesToTerms: true,
      iAmForeign: isForeignBuyer,
      foreignIdentityType: getDefaultForeignIdentityType(buyer.identityType, isForeignBuyer, buyer.countryCode),
      zipCode: buyer.address?.zipCode,
    };
  }

  return {
    email: '',
    emailConfirmation: '',
    firstName: '',
    lastName: '',
    phone: '+55',
    identity: '',
    corporateName: '',
    countryCode: CountryCode.BRAZIL,
    identityType: IdentityType.CPF,
    iAmForeign: false,
    birthdate: undefined,
    agreesToTerms: false,
    foreignIdentityType: ForeignIdentityType.FOREIGN_ID,
    zipCode: '',
  };
};

export const createPayload = (payload: FieldValues, recaptchaToken?: string | null) => {
  const editParams: UserRequest = {
    firstName: payload['firstName'],
    lastName: payload['lastName'],
    email: payload['email'],
    identity: payload['identity'],
    phone: payload['phone'],
    corporateName: payload['corporateName'] ?? null,
    countryCode: payload['countryCode'] ?? null,
    identityType: payload['identityType'] ?? 'cpf',
    birthdate: payload['birthdate'] ?? null,
    address: payload['address'],
  };

  const createParams: SignupRequest = {
    ...editParams,
    address: payload['address'],
    agreesToTerms: true,
    password: generatePassword(8),
    recaptchaToken: recaptchaToken ?? null,
  };

  return { createParams, editParams };
};
